import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { contentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Workspace = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('ad-copy');
  const [speechHistory, setSpeechHistory] = useState([]);
  const [imageHistory, setImageHistory] = useState([]);
  const [aiImageHistory, setAiImageHistory] = useState([]);
  const [adCopyHistory, setAdCopyHistory] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [blogHistory, setBlogHistory] = useState([]);
  const [productDescHistory, setProductDescHistory] = useState([]);
  const [posterHistory, setPosterHistory] = useState([]);
  const [marketSurveyHistory, setMarketSurveyHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioRefs, setAudioRefs] = useState({});
  const [deletingItemId, setDeletingItemId] = useState(null); // Track which item is being deleted
  const [copiedProductDescId, setCopiedProductDescId] = useState(null); // Track copied product description

  // Load all data on component mount
  useEffect(() => {
    loadAdCopyHistory();
    loadSpeechHistory();
    loadImageHistory();
    loadAiImageHistory();
    loadEmailHistory();
    loadBlogHistory();
    loadProductDescHistory();
    loadPosterHistory();
    loadMarketSurveyHistory();
  }, []);

  // Reload data when switching tabs
  useEffect(() => {
    if (activeTab === 'ad-copy' && adCopyHistory.length === 0) {
      loadAdCopyHistory();
    } else if (activeTab === 'speech' && speechHistory.length === 0) {
      loadSpeechHistory();
    } else if (activeTab === 'images' && imageHistory.length === 0) {
      loadImageHistory();
    } else if (activeTab === 'ai-images' && aiImageHistory.length === 0) {
      loadAiImageHistory();
    } else if (activeTab === 'email' && emailHistory.length === 0) {
      loadEmailHistory();
    } else if (activeTab === 'blog' && blogHistory.length === 0) {
      loadBlogHistory();
    } else if (activeTab === 'product-desc' && productDescHistory.length === 0) {
      loadProductDescHistory();
    } else if (activeTab === 'poster' && posterHistory.length === 0) {
      loadPosterHistory();
    } else if (activeTab === 'market-survey' && marketSurveyHistory.length === 0) {
      loadMarketSurveyHistory();
    }
  }, [activeTab]);

  const loadAdCopyHistory = async () => {
    try {
      const response = await contentAPI.getHistory({ content_type: 'ad_copy', limit: 100 });
      if (response.data.success) {
        setAdCopyHistory(response.data.data.items || []);
      }
    } catch (err) {
      console.error('Failed to load ad copy history:', err);

      // Check for authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
        return;
      }

      setError('Failed to load ad copy history.');
    }
  };

  const loadSpeechHistory = async () => {
    try {
      const response = await contentAPI.getHistory({ content_type: 'speech', limit: 100 });
      if (response.data.success) {
        setSpeechHistory(response.data.data.items || []);
      }
    } catch (err) {
      console.error('Failed to load speech history:', err);

      // Check for authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Clear token and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
        return;
      }

      setError('Failed to load workspace history.');
    }
  };

  const loadImageHistory = async () => {
    try {
      const response = await contentAPI.getHistory({ content_type: 'image-composition', limit: 100 });
      if (response.data.success) {
        setImageHistory(response.data.data.items || []);
      }
    } catch (err) {
      console.error('Failed to load image history:', err);

      // Check for authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
        return;
      }

      setError('Failed to load image composition history.');
    } finally {
      // Only set loading to false after both attempts
      setLoading(false);
    }
  };

  const loadAiImageHistory = async () => {
    try {
      const response = await contentAPI.getHistory({ content_type: 'image', limit: 100 });
      if (response.data.success) {
        setAiImageHistory(response.data.data.items || []);
      }
    } catch (err) {
      console.error('Failed to load AI image history:', err);

      // Check for authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
        return;
      }

      setError('Failed to load AI image generation history.');
    }
  };

  const loadEmailHistory = async () => {
    try {
      const response = await contentAPI.getHistory({ content_type: 'email_beautify,email_reply', limit: 100 });
      if (response.data.success) {
        setEmailHistory(response.data.data.items || []);
      }
    } catch (err) {
      console.error('Failed to load email history:', err);

      // Check for authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
        return;
      }

      setError('Failed to load email generation history.');
    }
  };

  const loadBlogHistory = async () => {
    try {
      const response = await contentAPI.getHistory({ content_type: 'blog_post', limit: 100 });
      if (response.data.success) {
        setBlogHistory(response.data.data.items || []);
      }
    } catch (err) {
      console.error('Failed to load blog history:', err);

      // Check for authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
        return;
      }

      setError('Failed to load blog generation history.');
    }
  };

  const loadProductDescHistory = async () => {
    try {
      const response = await contentAPI.getHistory({ content_type: 'product_description', limit: 100 });
      if (response.data.success) {
        setProductDescHistory(response.data.data.items || []);
      }
    } catch (err) {
      console.error('Failed to load product description history:', err);

      // Check for authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
        return;
      }

      setError('Failed to load product description history.');
    }
  };

  const loadPosterHistory = async () => {
    try {
      const response = await contentAPI.getHistory({ content_type: 'poster', limit: 100 });
      if (response.data.success) {
        setPosterHistory(response.data.data.items || []);
      }
    } catch (err) {
      console.error('Failed to load poster history:', err);

      // Check for authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
        return;
      }

      setError('Failed to load poster history.');
    } finally {
      setLoading(false);
    }
  };

  const loadMarketSurveyHistory = async () => {
    try {
      const response = await contentAPI.getHistory({ content_type: 'market-survey', limit: 100 });
      if (response.data.success) {
        setMarketSurveyHistory(response.data.data.items || []);
      }
    } catch (err) {
      console.error('Failed to load market survey history:', err);

      // Check for authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
        return;
      }

      setError('Failed to load market survey history.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewMarketSurvey = (item) => {
    try {
      const inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
      const reportData = typeof item.generated_content === 'string' ? JSON.parse(item.generated_content) : item.generated_content;
      const metadata = typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata;

      navigate('/market-survey', {
        state: {
          savedReport: {
            id: item.id,
            ...reportData,
            productName: inputData.productName,
            productDescription: inputData.productDescription,
            productCategory: inputData.productCategory,
            targetMarket: inputData.targetMarket,
            competitors: metadata?.competitors,
            additionalInfo: metadata?.additionalInfo,
            createdAt: item.created_at
          }
        }
      });
    } catch (err) {
      console.error('Failed to parse market survey data:', err);
      setError('Failed to load market survey report');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleEditAdCopy = (item) => {
    try {
      const inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
      const generatedData = typeof item.generated_content === 'string' ? JSON.parse(item.generated_content) : item.generated_content;

      navigate('/ad-copy', {
        state: {
          prefillData: {
            // Basic fields
            productName: inputData?.productName || '',
            productDescription: inputData?.productDescription || '',
            targetAudience: inputData?.targetAudience || '',

            // Customization
            tone: inputData?.tone || 'professional',
            language: inputData?.language || 'en-US',

            // Advanced fields
            keywords: inputData?.keywords || '',
            cta: inputData?.cta || '',
            brandVoice: inputData?.brandVoice || '',
            painPoints: inputData?.painPoints || '',
            benefits: inputData?.benefits || '',
            competitors: inputData?.competitors || '',

            // Generation options
            numVariations: inputData?.numVariations || 3,
            maxLength: inputData?.maxLength || 'medium',

            // Previous generated variations
            previousVariations: generatedData || []
          }
        }
      });
    } catch (err) {
      console.error('Failed to parse ad copy data:', err);
      navigate('/ad-copy');
    }
  };

  const handleEditSpeech = (item) => {
    navigate('/speech-synthesis', {
      state: {
        prefillData: {
          text: item.input_text || '',
          audioUrl: item.generated_content || '',
          voice: extractVoiceFromItem(item)
        }
      }
    });
  };

  const handleEditImage = (item) => {
    // Extract metadata from the image composition record
    try {
      const inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
      navigate('/image-composition', {
        state: {
          prefillData: {
            prompt: inputData?.prompt || '',
            style: inputData?.style || 'natural',
            previousImage: item.generated_content || ''
          }
        }
      });
    } catch (err) {
      console.error('Failed to parse image composition data:', err);
      navigate('/image-composition');
    }
  };

  const handleEditAiImage = (item) => {
    // Extract metadata from the AI image generation record
    try {
      const inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
      navigate('/image-generation', {
        state: {
          prefillData: {
            prompt: inputData?.prompt || '',
            negativePrompt: inputData?.negativePrompt || '',
            size: inputData?.size || '1024x1024',
            style: inputData?.style || 'vivid',
            previousImage: item.generated_content || ''
          }
        }
      });
    } catch (err) {
      console.error('Failed to parse AI image data:', err);
      navigate('/image-generation');
    }
  };

  const handleEditEmail = (item) => {
    try {
      const inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
      const generatedData = typeof item.generated_content === 'string' ? JSON.parse(item.generated_content) : item.generated_content;

      // Determine mode based on content_type
      const mode = item.content_type === 'email_reply' ? 'reply' : 'beautify';

      navigate('/email-marketing', {
        state: {
          mode: mode,
          prefillData: {
            subject: inputData?.subject || '',
            content: inputData?.content || '',
            tone: inputData?.tone || 'professional',
            language: inputData?.language || 'en',
            receivedEmail: inputData?.receivedEmail || '',
            replyTone: inputData?.tone || 'professional',
            keyPoints: inputData?.keyPoints || '',
            // Include generated content for reference
            generatedSubject: generatedData?.subject || '',
            generatedContent: generatedData?.content || generatedData?.reply || ''
          }
        }
      });
    } catch (err) {
      console.error('Failed to parse email data:', err);
      navigate('/email-marketing');
    }
  };

  const handleEditBlog = (item) => {
    try {
      const generatedData = typeof item.generated_content === 'string' ? JSON.parse(item.generated_content) : item.generated_content;
      const metadata = typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata;

      navigate('/blog-posts', {
        state: {
          prefillData: {
            topic: item.input_text || '',
            blogType: metadata?.blogType || 'how-to',
            targetAudience: metadata?.targetAudience || '',
            wordCount: metadata?.wordCount || '1000-1500',
            tone: metadata?.tone || 'informative',
            writingStyle: metadata?.writingStyle || 'conversational',
            language: metadata?.language || 'en',
            keywords: metadata?.keywords?.join(', ') || '',
            // Include generated content for reference
            generatedTitle: generatedData?.title || '',
            generatedContent: generatedData?.fullContent || '',
            generatedSeoMeta: generatedData?.seoMeta || null
          }
        }
      });
    } catch (err) {
      console.error('Failed to parse blog data:', err);
      navigate('/blog-posts');
    }
  };

  const handleEditPoster = (item) => {
    try {
      const inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;

      navigate('/poster', {
        state: {
          prefillData: {
            title: inputData?.title || '',
            subTitle: inputData?.subTitle || '',
            bodyText: inputData?.bodyText || '',
            prompt: inputData?.promptText || '',
            language: inputData?.language || 'english',
            aspectRatio: inputData?.aspectRatio || 'portrait',
            loraStyle: inputData?.loraStyle || 'modern',
            loraWeight: inputData?.loraWeight || 0.8,
            previousImage: item.generated_content || ''
          }
        }
      });
    } catch (err) {
      console.error('Failed to parse poster data:', err);
      navigate('/poster');
    }
  };

  const extractVoiceFromItem = (item) => {
    try {
      const metadata = typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata;
      return metadata?.voice || 'Cherry';
    } catch {
      return 'Cherry';
    }
  };

  const handlePlayAudio = async (audioUrl, itemId) => {
    if (playingAudio && playingAudio !== itemId) {
      const prevAudio = audioRefs[playingAudio];
      if (prevAudio) {
        prevAudio.pause();
        prevAudio.currentTime = 0;
      }
    }

    const audio = audioRefs[itemId];
    if (audio) {
      if (playingAudio === itemId) {
        if (audio.paused) {
          audio.play();
          setPlayingAudio(itemId); // Force re-render
        } else {
          audio.pause();
          setPlayingAudio(null); // Update state to show pause icon
        }
      } else {
        audio.play();
        setPlayingAudio(itemId);
      }
    } else {
      const fullAudioUrl = `https://api.getaizest.com${audioUrl}`;

      // Check if audio is accessible (authentication check)
      try {
        const response = await fetch(fullAudioUrl, {
          method: 'HEAD',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        // If unauthorized, redirect to login
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          navigate('/login', { replace: true });
          return;
        }

        // If other error, show error message
        if (!response.ok) {
          setError('Failed to load audio file');
          setTimeout(() => setError(''), 3000);
          return;
        }
      } catch (err) {
        console.error('Audio access check failed:', err);
        setError('Failed to check audio availability');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // Create and play audio
      const newAudio = new Audio(fullAudioUrl);

      // Add event listeners for state updates
      newAudio.addEventListener('ended', () => setPlayingAudio(null));
      newAudio.addEventListener('pause', () => {
        // Only update if this is the currently playing audio
        if (playingAudio === itemId) {
          setPlayingAudio(null);
        }
      });
      newAudio.addEventListener('play', () => setPlayingAudio(itemId));

      newAudio.addEventListener('error', async (e) => {
        console.error('Audio playback error:', e);

        // Try to determine if it's an auth error by checking the URL again
        try {
          const checkResponse = await fetch(fullAudioUrl, {
            method: 'HEAD',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (checkResponse.status === 401 || checkResponse.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            navigate('/login', { replace: true });
            return;
          }
        } catch (checkErr) {
          console.error('Auth check failed:', checkErr);
        }

        setError('Failed to play audio');
        setTimeout(() => setError(''), 3000);
      });

      setAudioRefs(prev => ({ ...prev, [itemId]: newAudio }));
      newAudio.play();
      setPlayingAudio(itemId);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await contentAPI.deleteContent(itemId);

      // Clear the deleting state
      setDeletingItemId(null);

      // Refresh the appropriate history based on active tab
      if (activeTab === 'ad-copy') {
        loadAdCopyHistory();
      } else if (activeTab === 'speech') {
        loadSpeechHistory();
      } else if (activeTab === 'images') {
        loadImageHistory();
      } else if (activeTab === 'ai-images') {
        loadAiImageHistory();
      } else if (activeTab === 'email') {
        loadEmailHistory();
      } else if (activeTab === 'blog') {
        loadBlogHistory();
      } else if (activeTab === 'product-desc') {
        loadProductDescHistory();
      } else if (activeTab === 'poster') {
        loadPosterHistory();
      } else if (activeTab === 'market-survey') {
        loadMarketSurveyHistory();
      }
    } catch (err) {
      console.error('Failed to delete item:', err);

      // Check for authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/login', { replace: true });
        return;
      }

      setError('Failed to delete item.');
      setDeletingItemId(null);
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const truncateText = (text, maxLength = 120) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-4 py-4">
            {/* Compact Header */}
            <div className="mb-4">
              <h1 className="text-2xl font-semibold text-gray-900">Workspace</h1>
              <p className="text-sm text-gray-500 mt-0.5">Manage your content</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center">
                <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            {/* Compact Tab Navigation */}
            <div className="mb-3 border-b border-gray-200">
              <nav className="-mb-px flex space-x-3">
                <button
                  onClick={() => setActiveTab('ad-copy')}
                  className={`py-2 px-1 border-b-2 text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'ad-copy'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Ad Copy ({adCopyHistory.length})
                </button>
                <button
                  onClick={() => setActiveTab('speech')}
                  className={`py-2 px-1 border-b-2 text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'speech'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Speech ({speechHistory.length})
                </button>
                <button
                  onClick={() => setActiveTab('images')}
                  className={`py-2 px-1 border-b-2 text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'images'
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Images ({imageHistory.length})
                </button>
                <button
                  onClick={() => setActiveTab('ai-images')}
                  className={`py-2 px-1 border-b-2 text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'ai-images'
                      ? 'border-pink-500 text-pink-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  AI Images ({aiImageHistory.length})
                </button>
                <button
                  onClick={() => setActiveTab('email')}
                  className={`py-2 px-1 border-b-2 text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'email'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Email ({emailHistory.length})
                </button>
                <button
                  onClick={() => setActiveTab('blog')}
                  className={`py-2 px-1 border-b-2 text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'blog'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Blog ({blogHistory.length})
                </button>
                <button
                  onClick={() => setActiveTab('product-desc')}
                  className={`py-2 px-1 border-b-2 text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'product-desc'
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Products ({productDescHistory.length})
                </button>
                <button
                  onClick={() => setActiveTab('poster')}
                  className={`py-2 px-1 border-b-2 text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'poster'
                      ? 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Posters ({posterHistory.length})
                </button>
                <button
                  onClick={() => setActiveTab('market-survey')}
                  className={`py-2 px-1 border-b-2 text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'market-survey'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Research ({marketSurveyHistory.length})
                </button>
              </nav>
            </div>

            {/* Content Area */}
            {activeTab === 'ad-copy' && (
              <div>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-green-500 border-t-transparent"></div>
                    <p className="mt-3 text-sm text-gray-600">Loading...</p>
                  </div>
                ) : adCopyHistory.length > 0 ? (
                  <div className="space-y-2">
                    {adCopyHistory.map((item) => {
                      let inputData = {};
                      let generatedData = [];
                      try {
                        inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
                        generatedData = typeof item.generated_content === 'string' ? JSON.parse(item.generated_content) : item.generated_content;
                      } catch (e) {
                        console.error('Failed to parse ad copy data:', e);
                      }

                      return (
                        <div
                          key={item.id}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-start gap-4">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                              </svg>
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                                  {inputData?.tone || 'professional'}
                                </span>
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                                  {inputData?.language || 'en-US'}
                                </span>
                              </div>
                              <div className="mb-2">
                                <h3 className="text-sm font-semibold text-gray-900">{inputData?.productName || 'Untitled Ad'}</h3>
                                <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                                  {inputData?.productDescription || 'No description'}
                                </p>
                              </div>
                              {generatedData && generatedData.length > 0 && generatedData[0].headline && (
                                <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-100">
                                  <p className="text-sm font-medium text-gray-900 mb-1">{generatedData[0].headline}</p>
                                  <p className="text-xs text-gray-600 line-clamp-2">{generatedData[0].description}</p>
                                  {generatedData[0].cta && (
                                    <p className="text-xs text-green-600 font-medium mt-1">{generatedData[0].cta}</p>
                                  )}
                                </div>
                              )}
                              <div className="mt-2 text-xs text-gray-500">
                                {generatedData?.length || 0} variation{generatedData?.length !== 1 ? 's' : ''} generated
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <button
                                onClick={() => handleEditAdCopy(item)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                                title="Edit & Re-generate"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  const copyText = generatedData && generatedData.length > 0 && generatedData[0].headline
                                    ? `${generatedData[0].headline}\n\n${generatedData[0].description}\n\n${generatedData[0].cta || ''}`
                                    : inputData?.productDescription || '';
                                  navigator.clipboard.writeText(copyText);
                                }}
                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                title="Copy content"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>

                              {/* Delete button with inline confirmation */}
                              {deletingItemId === item.id ? (
                                <div className="flex items-center gap-1.5 ml-2">
                                  <span className="text-xs text-gray-600 font-medium whitespace-nowrap">Delete?</span>
                                  <button
                                    onClick={() => handleDeleteItem(item.id)}
                                    className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() => setDeletingItemId(null)}
                                    className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded transition-colors"
                                  >
                                    No
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeletingItemId(item.id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                    <h3 className="mt-3 text-sm font-medium text-gray-900">No ad copy yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Create your first ad campaign</p>
                    <button
                      onClick={() => navigate('/ad-copy')}
                      className="mt-4 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'speech' && (
              <div>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-blue-500 border-t-transparent"></div>
                    <p className="mt-3 text-sm text-gray-600">Loading...</p>
                  </div>
                ) : speechHistory.length > 0 ? (
                  <div className="space-y-2">
                    {speechHistory.map((item) => (
                      <div
                        key={item.id}
                        className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-start gap-3">
                          {/* Play Button */}
                          <button
                            onClick={() => handlePlayAudio(item.generated_content, item.id)}
                            className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                              playingAudio === item.id
                                ? 'bg-blue-600 hover:bg-blue-700'
                                : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                            title="Play audio"
                          >
                            {playingAudio === item.id && audioRefs[item.id] && !audioRefs[item.id].paused ? (
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className={`w-4 h-4 ${playingAudio === item.id ? 'text-white' : 'text-gray-600'}`} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed line-clamp-2">
                              {truncateText(item.input_text)}
                            </p>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => handleEditSpeech(item)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Re-create"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <a
                              href={`https://api.getaizest.com${item.generated_content}`}
                              download
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Download"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </a>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(item.input_text || '');
                              }}
                              className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="Copy text"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>

                            {/* Delete button with inline confirmation */}
                            {deletingItemId === item.id ? (
                              <div className="flex items-center gap-1.5 ml-2">
                                <span className="text-xs text-gray-600 font-medium whitespace-nowrap">Delete?</span>
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                                >
                                  Yes
                                </button>
                                <button
                                  onClick={() => setDeletingItemId(null)}
                                  className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded transition-colors"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeletingItemId(item.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <h3 className="mt-3 text-sm font-medium text-gray-900">No content yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Create your first speech synthesis</p>
                    <button
                      onClick={() => navigate('/speech-synthesis')}
                      className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Image Composition Tab */}
            {activeTab === 'images' && (
              <div>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-purple-500 border-t-transparent"></div>
                    <p className="mt-3 text-sm text-gray-600">Loading...</p>
                  </div>
                ) : imageHistory.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {imageHistory.map((item) => {
                      const inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
                      return (
                        <div
                          key={item.id}
                          className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow group"
                        >
                          {/* Image Preview */}
                          <div className="relative aspect-video bg-gray-100">
                            <img
                              src={`https://api.getaizest.com${item.generated_content}`}
                              alt="Composed"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                              }}
                            />
                            {/* Overlay buttons on hover */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                              <button
                                onClick={() => handleEditImage(item)}
                                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg transition-colors"
                                title="Edit & Re-compose"
                              >
                                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <a
                                href={`https://api.getaizest.com${item.generated_content}`}
                                download
                                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors"
                                title="Download"
                              >
                                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download
                              </a>
                            </div>
                          </div>

                          {/* Content Info */}
                          <div className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                                {inputData?.style || 'natural'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 line-clamp-2 mb-3">
                              {inputData?.prompt || 'No description'}
                            </p>

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(inputData?.prompt || '');
                                  }}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                  title="Copy prompt"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                </button>

                                {/* Delete button with inline confirmation */}
                                {deletingItemId === item.id ? (
                                  <div className="flex items-center gap-1.5 ml-2">
                                    <span className="text-xs text-gray-600 font-medium whitespace-nowrap">Delete?</span>
                                    <button
                                      onClick={() => handleDeleteItem(item.id)}
                                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                                    >
                                      Yes
                                    </button>
                                    <button
                                      onClick={() => setDeletingItemId(null)}
                                      className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded transition-colors"
                                    >
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setDeletingItemId(item.id)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                              <span className="text-xs text-gray-400">
                                {inputData?.imageCount || 2} images
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 className="mt-3 text-sm font-medium text-gray-900">No compositions yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Create your first image composition</p>
                    <button
                      onClick={() => navigate('/image-composition')}
                      className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* AI Images Tab */}
            {activeTab === 'ai-images' && (
              <div>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-pink-500 border-t-transparent"></div>
                    <p className="mt-3 text-sm text-gray-600">Loading...</p>
                  </div>
                ) : aiImageHistory.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {aiImageHistory.map((item) => {
                      let inputData = {};
                      try {
                        inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
                      } catch (e) {
                        console.error('Failed to parse input data:', e);
                      }
                      return (
                        <div
                          key={item.id}
                          className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow group"
                        >
                          {/* Image Preview */}
                          <div className="relative aspect-square bg-gray-100">
                            <img
                              src={item.generated_content.startsWith('http') ? item.generated_content : `https://api.getaizest.com${item.generated_content}`}
                              alt="AI Generated"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                              }}
                            />
                            {/* Overlay buttons on hover */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                              <button
                                onClick={() => handleEditAiImage(item)}
                                className="px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-xs font-medium rounded-lg transition-colors"
                                title="Edit & Re-generate"
                              >
                                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <a
                                href={item.generated_content.startsWith('http') ? item.generated_content : `https://api.getaizest.com${item.generated_content}`}
                                download
                                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors"
                                title="Download"
                              >
                                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download
                              </a>
                            </div>
                          </div>

                          {/* Content Info */}
                          <div className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                              <span className="px-2 py-0.5 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-xs font-medium rounded">
                                {inputData?.style || 'vivid'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 line-clamp-2 mb-3">
                              {inputData?.prompt || 'No prompt'}
                            </p>
                            {inputData?.negativePrompt && (
                              <p className="text-xs text-gray-500 line-clamp-1 mb-2">
                                <span className="font-medium">Negative:</span> {inputData.negativePrompt}
                              </p>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(inputData?.prompt || '');
                                  }}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                  title="Copy prompt"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                </button>

                                {/* Delete button with inline confirmation */}
                                {deletingItemId === item.id ? (
                                  <div className="flex items-center gap-1.5 ml-2">
                                    <span className="text-xs text-gray-600 font-medium whitespace-nowrap">Delete?</span>
                                    <button
                                      onClick={() => handleDeleteItem(item.id)}
                                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                                    >
                                      Yes
                                    </button>
                                    <button
                                      onClick={() => setDeletingItemId(null)}
                                      className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded transition-colors"
                                    >
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setDeletingItemId(item.id)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                              <span className="text-xs text-gray-400">
                                {inputData?.size || '1024x1024'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-pink-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 className="mt-3 text-sm font-medium text-gray-900">No AI images yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Generate your first AI image</p>
                    <button
                      onClick={() => navigate('/image-generation')}
                      className="mt-4 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Email Generation Tab */}
            {activeTab === 'email' && (
              <div>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-orange-500 border-t-transparent"></div>
                    <p className="mt-3 text-sm text-gray-600">Loading...</p>
                  </div>
                ) : emailHistory.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4">
                    {emailHistory.map((item) => {
                      let inputData = {};
                      let generatedData = {};
                      try {
                        inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
                        generatedData = typeof item.generated_content === 'string' ? JSON.parse(item.generated_content) : item.generated_content;
                      } catch (e) {
                        console.error('Failed to parse email data:', e);
                      }

                      const isReply = item.content_type === 'email_reply';
                      const emailContent = generatedData?.content || generatedData?.reply || '';
                      const subject = generatedData?.subject || inputData?.subject || '';

                      return (
                        <div
                          key={item.id}
                          className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                        >
                          {/* Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  isReply
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-orange-100 text-orange-800'
                                }`}>
                                  {isReply ? (
                                    <>
                                      <svg className="w-3 h-3 mr-1" viewBox="0 0 1026 1024" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M654.222222 631.466667h176.355556c68.266667 0 136.533333 39.822222 170.666666 96.711111 34.133333 62.577778 34.133333 136.533333 0 199.111111s-102.4 96.711111-170.666666 96.711111h-79.644445c-11.377778 0-28.444444-5.688889-34.133333-17.066667-5.688889-11.377778-5.688889-28.444444 0-39.822222 5.688889-11.377778 22.755556-17.066667 34.133333-17.066667h79.644445c62.577778 0 119.466667-51.2 119.466666-119.466666 0-62.577778-51.2-119.466667-119.466666-119.466667h-176.355556l45.511111 45.511111c17.066667 17.066667 17.066667 39.822222 0 56.888889-17.066667 17.066667-39.822222 17.066667-56.888889 0l-113.777777-113.777778c-5.688889-5.688889-11.377778-17.066667-11.377778-28.444444s5.688889-22.755556 11.377778-28.444445l113.777777-113.777777c11.377778-11.377778 22.755556-11.377778 39.822223-11.377778 11.377778 5.688889 22.755556 17.066667 28.444444 28.444444 5.688889 11.377778 0 28.444444-11.377778 39.822223l-45.511111 45.511111z m-102.4 312.888889c11.377778 0 28.444444 5.688889 34.133334 17.066666 5.688889 11.377778 5.688889 28.444444 0 39.822222-5.688889 17.066667-22.755556 22.755556-34.133334 22.755556H119.466667C51.2 1024 0 972.8 0 904.533333V119.466667C0 51.2 51.2 0 119.466667 0h711.111111c62.577778 0 119.466667 51.2 119.466666 119.466667V512c0 11.377778-5.688889 28.444444-17.066666 34.133333-11.377778 5.688889-28.444444 5.688889-39.822222 0-11.377778-5.688889-17.066667-22.755556-17.066667-34.133333V119.466667c0-22.755556-17.066667-39.822222-39.822222-39.822223H119.466667c-22.755556 0-39.822222 17.066667-39.822223 39.822223v785.066666c0 22.755556 17.066667 39.822222 39.822223 39.822223h432.355555zM238.933333 199.111111h472.177778c22.755556 0 39.822222 17.066667 39.822222 39.822222 0 22.755556-17.066667 39.822222-39.822222 39.822223H238.933333c-22.755556 0-39.822222-17.066667-39.822222-39.822223 0-22.755556 17.066667-39.822222 39.822222-39.822222z m0 233.244445h238.933334c17.066667 0 34.133333 17.066667 34.133333 39.822222s-17.066667 39.822222-39.822222 39.822222H238.933333c-22.755556 0-39.822222-17.066667-39.822222-39.822222s17.066667-39.822222 39.822222-39.822222z m0 0" fill="currentColor" />
                                      </svg>
                                      Reply
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-3 h-3 mr-1" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M951.9 450.2l-98.1-131.5 52.7-155.4c4.4-13 1.1-27.3-8.6-37s-24-13-37-8.6l-155.4 52.7L574 72.3c-11-8.2-25.7-9.4-37.9-3.2s-19.8 18.8-19.7 32.5l2.1 164-133.9 94.7c-11.2 7.9-16.9 21.5-14.8 35 2.1 13.5 11.8 24.7 24.9 28.7l117.8 36.6L74.6 897.8c-14.1 14-14.1 36.8 0 50.9 7 7 16.3 10.6 25.5 10.6s18.4-3.5 25.4-10.5l437.9-437.1L600 629.5c4.1 13.1 15.2 22.7 28.7 24.9 1.9 0.3 3.8 0.4 5.6 0.4 11.6 0 22.6-5.6 29.4-15.2l94.7-133.9 164 2.1c13.7 0.2 26.3-7.4 32.5-19.7 6.5-12.2 5.2-26.9-3-37.9z m-211.4-16.8c-11.8-0.1-23 5.5-29.9 15.2l-63.5 89.8-32.7-105.1c-3.5-11.3-12.4-20.2-23.7-23.7l-105-32.6 89.8-63.5c9.7-6.8 15.4-18 15.2-29.9l-1.4-110 88.2 65.8c9.5 7.1 21.9 9 33.1 5.2l104.2-35.3-35.3 104.2c-3.8 11.2-1.8 23.6 5.2 33.1l65.8 88.2-110-1.4z" fill="currentColor" />
                                      </svg>
                                      Beautify
                                    </>
                                  )}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(item.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              {subject && (
                                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                                  {subject}
                                </h3>
                              )}
                            </div>
                          </div>

                          {/* Email Content Preview */}
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <p className="text-sm text-gray-700 line-clamp-3">
                              {emailContent}
                            </p>
                          </div>

                          {/* Metadata */}
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                            <div className="flex items-center gap-3">
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                </svg>
                                {inputData?.language || 'en'}
                              </span>
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                </svg>
                                {inputData?.tone || 'professional'}
                              </span>
                              <span>{emailContent.length} chars</span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditEmail(item)}
                              className="flex-1 px-3 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit & Regenerate
                            </button>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(emailContent);
                                alert('Email copied to clipboard!');
                              }}
                              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                              title="Copy Email"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>

                            {/* Delete button with inline confirmation */}
                            {deletingItemId === item.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-600 font-medium">Delete?</span>
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors"
                                >
                                  Yes
                                </button>
                                <button
                                  onClick={() => setDeletingItemId(null)}
                                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded-lg transition-colors"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeletingItemId(item.id)}
                                className="px-3 py-2 bg-gray-100 hover:bg-red-50 text-red-600 text-sm font-medium rounded-lg transition-colors"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <h3 className="mt-3 text-sm font-medium text-gray-900">No email generations yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Create your first email with AI assistance</p>
                    <button
                      onClick={() => navigate('/email-marketing')}
                      className="mt-4 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Blog Posts Tab */}
            {activeTab === 'blog' && (
              <div>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-indigo-500 border-t-transparent"></div>
                    <p className="mt-3 text-sm text-gray-600">Loading...</p>
                  </div>
                ) : blogHistory.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4">
                    {blogHistory.map((item) => {
                      let generatedData = {};
                      let metadata = {};
                      try {
                        generatedData = typeof item.generated_content === 'string' ? JSON.parse(item.generated_content) : item.generated_content;
                        metadata = typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata;
                      } catch (e) {
                        console.error('Failed to parse blog data:', e);
                      }

                      const blogTitle = generatedData?.title || item.input_text || 'Untitled Blog';
                      const blogContent = generatedData?.fullContent || '';
                      const wordCount = generatedData?.stats?.wordCount || 0;
                      const readingTime = generatedData?.stats?.readingTime || '0 min';

                      return (
                        <div
                          key={item.id}
                          className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                        >
                          {/* Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                  <svg className="w-3 h-3 mr-1" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M512 348.028928c-64.937984 0-124.901376 21.38112-173.221888 57.491456V146.255872c0-27.404288-26.079232-49.620992-58.232832-49.620992-32.147456 0-58.228736 22.216704-58.228736 49.620992V651.204608c0 2.799616 0.28672 5.541888 0.811008 8.214528 11.114496 149.829632 136.2432 267.945984 288.872448 267.945984 160.02048 0 289.681408-129.691648 289.681408-289.66912S672.02048 348.028928 512 348.028928z m0 479.862784c-90.0096 0-165.451776-62.5664-185.174016-146.5856a190.754816 190.754816 0 0 1-5.031936-43.608064c0-28.049408 6.080512-54.677504 16.984064-78.647296 29.93152-65.796096 96.251904-111.54432 173.221888-111.54432 105.070592 0 190.208 85.153792 190.208 190.193664 0 105.035776-85.137408 190.191616-190.208 190.191616z" fill="currentColor" />
                                  </svg>
                                  {metadata?.blogType?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Blog Post'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(item.created_at).toLocaleDateString()}
                                </span>
                                {metadata?.language && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                                    🌐 {metadata.language.toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2">
                                {blogTitle}
                              </h3>
                            </div>
                          </div>

                          {/* Blog Content Preview */}
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <p className="text-sm text-gray-700 line-clamp-3">
                              {blogContent.replace(/[#*_\[\]]/g, '').substring(0, 200)}...
                            </p>
                          </div>

                          {/* Metadata */}
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                            <div className="flex items-center gap-3">
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {wordCount} words
                              </span>
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {readingTime}
                              </span>
                              {metadata?.tone && (
                                <span className="flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                  </svg>
                                  {metadata.tone}
                                </span>
                              )}
                              {metadata?.imageCount > 0 && (
                                <span className="flex items-center text-green-600">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  {metadata.imageCount} images
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditBlog(item)}
                              className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                              title="Edit & Regenerate"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                const contentToCopy = `${blogTitle}\n\n${blogContent}`;
                                navigator.clipboard.writeText(contentToCopy);
                              }}
                              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                              title="Copy"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>

                            {/* Delete button with inline confirmation */}
                            {deletingItemId === item.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-600 font-medium">Delete this blog?</span>
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => setDeletingItemId(null)}
                                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded-lg transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeletingItemId(item.id)}
                                className="px-3 py-2 bg-gray-100 hover:bg-red-50 text-red-600 text-sm font-medium rounded-lg transition-colors"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="mt-3 text-sm font-medium text-gray-900">No blog posts yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Create your first AI-powered blog post</p>
                    <button
                      onClick={() => navigate('/blog-posts')}
                      className="mt-4 px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Product Descriptions Tab */}
            {activeTab === 'product-desc' && (
              <div>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-teal-500 border-t-transparent"></div>
                    <p className="mt-3 text-sm text-gray-600">Loading...</p>
                  </div>
                ) : productDescHistory.length > 0 ? (
                  <div className="space-y-2">
                    {productDescHistory.map((item) => {
                      const metadata = typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata;
                      const generatedData = typeof item.generated_content === 'string' ? JSON.parse(item.generated_content) : item.generated_content;

                      return (
                        <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition relative">
                          {/* Delete Button */}
                          {deletingItemId === item.id ? (
                            <div className="absolute top-4 right-4 flex items-center gap-2">
                              <span className="text-xs text-gray-600 font-medium whitespace-nowrap">Delete?</span>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => setDeletingItemId(null)}
                                className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded transition-colors"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeletingItemId(item.id)}
                              className="absolute top-4 right-4 text-gray-400 hover:text-red-600 transition"
                              title="Delete"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}

                          <div className="mb-4 pr-10">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{generatedData.productTitle || item.input_text}</h3>
                                <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-3">
                                  {metadata.productCategory && (
                                    <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded">
                                      {metadata.productCategory}
                                    </span>
                                  )}
                                  {metadata.brandName && (
                                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                      {metadata.brandName}
                                    </span>
                                  )}
                                  {metadata.pricePoint && (
                                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                                      {metadata.pricePoint}
                                    </span>
                                  )}
                                  {metadata.writingStyle && (
                                    <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded">
                                      {metadata.writingStyle}
                                    </span>
                                  )}
                                  {metadata.language && metadata.language !== 'en' && (
                                    <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded">
                                      {metadata.language.toUpperCase()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {generatedData.shortDescription && (
                              <p className="text-gray-700 mb-3 leading-relaxed">{generatedData.shortDescription}</p>
                            )}

                            {generatedData.longDescription && (
                              <div className="text-gray-600 text-sm leading-relaxed mb-3 whitespace-pre-line">
                                {generatedData.longDescription.substring(0, 200)}
                                {generatedData.longDescription.length > 200 && '...'}
                              </div>
                            )}

                            {generatedData.featureBullets && generatedData.featureBullets.length > 0 && (
                              <div className="mb-3">
                                <p className="text-xs font-semibold text-gray-700 mb-1">Key Features:</p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {generatedData.featureBullets.slice(0, 3).map((bullet, idx) => (
                                    <li key={idx} className="flex items-start">
                                      <span className="text-teal-600 mr-2">✓</span>
                                      <span>{bullet}</span>
                                    </li>
                                  ))}
                                  {generatedData.featureBullets.length > 3 && (
                                    <li className="text-xs text-gray-500 ml-5">+ {generatedData.featureBullets.length - 3} more features</li>
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="text-xs text-gray-500">
                              {new Date(item.created_at).toLocaleString()}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(JSON.stringify(generatedData, null, 2));
                                  setCopiedProductDescId(item.id);
                                  setTimeout(() => setCopiedProductDescId(null), 2000);
                                }}
                                className="px-3 py-1.5 text-sm bg-teal-50 text-teal-700 rounded hover:bg-teal-100 transition"
                              >
                                {copiedProductDescId === item.id ? '✓ Copied' : 'Copy'}
                              </button>
                              <button
                                onClick={() => {
                                  navigate('/product-descriptions', {
                                    state: {
                                      prefillData: {
                                        productName: item.input_text,
                                        ...metadata
                                      },
                                      generatedData: generatedData
                                    }
                                  });
                                }}
                                className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition"
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <h3 className="mt-3 text-sm font-medium text-gray-900">No Product Descriptions Yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by creating your first product description</p>
                    <button
                      onClick={() => navigate('/product-descriptions')}
                      className="mt-4 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition"
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Posters Tab */}
            {activeTab === 'poster' && (
              <div>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-red-500 border-t-transparent"></div>
                    <p className="mt-3 text-sm text-gray-600">Loading...</p>
                  </div>
                ) : posterHistory.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {posterHistory.map((item) => {
                      let inputData = {};
                      try {
                        inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
                      } catch (e) {
                        console.error('Failed to parse poster input data:', e);
                      }
                      return (
                        <div
                          key={item.id}
                          className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow group"
                        >
                          {/* Poster Preview */}
                          <div className="relative aspect-square bg-gray-100">
                            <img
                              src={item.generated_content}
                              alt="Generated poster"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                              }}
                            />
                            {/* Overlay buttons on hover */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                              <button
                                onClick={() => handleEditPoster(item)}
                                className="px-3 py-2 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white text-xs font-medium rounded-lg transition-colors"
                                title="Edit & Re-generate"
                              >
                                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <a
                                href={item.generated_content}
                                download
                                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors"
                                title="Download"
                              >
                                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download
                              </a>
                            </div>
                          </div>

                          {/* Content Info */}
                          <div className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                              <div className="flex items-center gap-1">
                                {inputData?.loraStyle && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                                    {inputData.loraStyle}
                                  </span>
                                )}
                                {inputData?.language && inputData.language !== 'english' && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                    {inputData.language}
                                  </span>
                                )}
                              </div>
                            </div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">
                              {inputData?.title || 'Untitled Poster'}
                            </h3>
                            {inputData?.subTitle && (
                              <p className="text-xs text-gray-600 line-clamp-1 mb-2">
                                {inputData.subTitle}
                              </p>
                            )}
                            {inputData?.bodyText && (
                              <p className="text-xs text-gray-500 line-clamp-2">
                                {inputData.bodyText}
                              </p>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-2">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(inputData?.title || '');
                                  }}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                  title="Copy title"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                </button>

                                {/* Delete button with inline confirmation */}
                                {deletingItemId === item.id ? (
                                  <div className="flex items-center gap-1.5 ml-2">
                                    <span className="text-xs text-gray-600 font-medium whitespace-nowrap">Delete?</span>
                                    <button
                                      onClick={() => handleDeleteItem(item.id)}
                                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                                    >
                                      Yes
                                    </button>
                                    <button
                                      onClick={() => setDeletingItemId(null)}
                                      className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded transition-colors"
                                    >
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setDeletingItemId(item.id)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                              <span className="text-xs text-gray-400">
                                {inputData?.aspectRatio || 'portrait'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 className="mt-3 text-sm font-medium text-gray-900">No posters yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Create your first AI-powered poster</p>
                    <button
                      onClick={() => navigate('/poster')}
                      className="mt-4 px-4 py-2 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Market Survey Tab */}
            {activeTab === 'market-survey' && (
              <div>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-indigo-500 border-t-transparent"></div>
                    <p className="mt-3 text-sm text-gray-600">Loading...</p>
                  </div>
                ) : marketSurveyHistory.length > 0 ? (
                  <div className="space-y-2">
                    {marketSurveyHistory.map((item) => {
                      let inputData = {};
                      try {
                        inputData = typeof item.input_text === 'string' ? JSON.parse(item.input_text) : item.input_text;
                      } catch (e) {
                        console.error('Failed to parse market survey input data:', e);
                      }
                      return (
                        <div
                          key={item.id}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-start gap-4">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                                {inputData?.productCategory && (
                                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                                    {inputData.productCategory}
                                  </span>
                                )}
                                {inputData?.targetMarket && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                                    {inputData.targetMarket}
                                  </span>
                                )}
                              </div>
                              <div className="mb-2">
                                <h3 className="text-sm font-semibold text-gray-900">{inputData?.productName || 'Market Research Report'}</h3>
                                <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                                  {inputData?.productDescription || 'Comprehensive market research and analysis report'}
                                </p>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <button
                                onClick={() => handleViewMarketSurvey(item)}
                                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                title="View Report"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>

                              {/* Delete button with inline confirmation */}
                              {deletingItemId === item.id ? (
                                <div className="flex items-center gap-1.5 ml-2">
                                  <span className="text-xs text-gray-600 font-medium whitespace-nowrap">Delete?</span>
                                  <button
                                    onClick={() => handleDeleteItem(item.id)}
                                    className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() => setDeletingItemId(null)}
                                    className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded transition-colors"
                                  >
                                    No
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeletingItemId(item.id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <h3 className="mt-3 text-sm font-medium text-gray-900">No market research yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Generate your first market research report</p>
                    <button
                      onClick={() => navigate('/market-survey')}
                      className="mt-4 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Workspace;
